import './PhotoUpload.css';

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import { scoringApp } from '../../../firebase';
import {
  AthletePhotoPayload,
  deleteAthleteProfilePhotos,
  uploadAthleteProfilePhotos,
  uploadAthleteScorePhoto,
} from '../../../services/athletePhotoService';
import { createImagePreview } from '../../../utils/imageUtils';
import CameraUploadButton from '../CameraUploadButton/CameraUploadButton';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import FullscreenPhotoViewer
  from '../FullscreenPhotoViewer/FullscreenPhotoViewer';
import IconButton from '../IconButton/IconButton';
import TrashIcon from '../TrashIcon';

const EMPTY_PAYLOAD: AthletePhotoPayload = {
  imageUrl: '',
  imageThumbnailUrl: '',
  storagePathFull: '',
  storagePathThumb: '',
};

export interface PhotoUploadProps {
  tryoutId: string;
  coachUid: string;
  uploadVariant?: 'profile' | 'score';
  disabled?: boolean;
  label?: string;
  className?: string;
  imageUrl?: string;
  storagePathFull?: string;
  storagePathThumb?: string;
  onImageUpload: (payload: AthletePhotoPayload) => void;
  onUploadingChange?: (uploading: boolean) => void;
  statusText?: string;
  /** Smaller empty state (~130×80); preview size unchanged after a photo is set. */
  compactEmpty?: boolean;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  tryoutId,
  coachUid,
  uploadVariant = 'profile',
  disabled = false,
  label = 'Athlete photo',
  className = '',
  imageUrl,
  storagePathFull,
  storagePathThumb,
  onImageUpload,
  onUploadingChange,
  statusText,
  compactEmpty = false,
}) => {
  const [imagePreview, setImagePreview] = useState<string | null>(imageUrl ?? null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setImagePreview(imageUrl?.trim() ? imageUrl : null);
  }, [imageUrl]);

  useEffect(() => {
    setImageLoading(Boolean(imagePreview));
  }, [imagePreview]);

  useEffect(() => {
    if (!imagePreview) {
      setViewerOpen(false);
    }
  }, [imagePreview]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setError('');
      const previewUrl = await createImagePreview(file);
      setImagePreview(previewUrl);
      await handleImageUpload(file);
    } catch (err: unknown) {
      console.error('Error handling image:', err);
      setError('Failed to process image');
      setImagePreview(null);
    }
  };

  const handleImageUpload = async (file: File): Promise<void> => {
    if (!tryoutId || !coachUid) {
      setError('Missing tryout or coach.');
      return;
    }
    try {
      setUploading(true);
      onUploadingChange?.(true);
      const result = uploadVariant === 'score'
        ? await uploadAthleteScorePhoto(scoringApp, tryoutId, coachUid, file)
        : await uploadAthleteProfilePhotos(scoringApp, tryoutId, coachUid, file);
      onImageUpload({
        imageUrl: result.imageUrl,
        imageThumbnailUrl: result.imageThumbnailUrl ?? '',
        storagePathFull: result.storagePathFull,
        storagePathThumb: result.storagePathThumb ?? '',
      });
    } catch (err: unknown) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image');
      setImagePreview(null);
      onImageUpload(EMPTY_PAYLOAD);
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  const handleRemoveImage = async (): Promise<void> => {
    setViewerOpen(false);
    const full = storagePathFull?.trim() ?? '';
    const thumb = storagePathThumb?.trim() ?? '';

    if (full || thumb) {
      try {
        setRemoving(true);
        setError('');
        onUploadingChange?.(true);
        await deleteAthleteProfilePhotos(scoringApp, full, thumb);
      } catch (err: unknown) {
        console.error('Error deleting image:', err);
        setError('Could not delete photo from storage.');
        return;
      } finally {
        setRemoving(false);
        onUploadingChange?.(false);
      }
    }

    setImagePreview(null);
    onImageUpload(EMPTY_PAYLOAD);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const busy = uploading || removing;
  const rootClass = [
    'photo-upload',
    className,
    compactEmpty ? 'photo-upload--compact-empty' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass}>
      {label ? <div className="photo-upload-label">{label}</div> : null}

      {!imagePreview ? (
        <div className="photo-upload-container">
          <CameraUploadButton
            className="photo-upload-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || busy}
            ariaLabel={label}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="photo-upload-hidden-input"
            onChange={(e) => {
              void handleImageChange(e);
            }}
            disabled={disabled || busy}
            aria-label={label}
          />
        </div>
      ) : (
        <div className="photo-upload-preview">
          <button
            type="button"
            className="photo-upload-preview-open"
            onClick={() => setViewerOpen(true)}
            disabled={busy}
            aria-label={`Open ${label} full screen`}
          >
            <span className="photo-upload-preview-frame">
              <span
                className={`photo-upload-preview-skeleton${imageLoading ? '' : ' photo-upload-preview-skeleton--hidden'}`}
                aria-hidden="true"
              />
              <img
                src={imagePreview}
                alt=""
                className={imageLoading ? 'photo-upload-preview-image photo-upload-preview-image--loading' : 'photo-upload-preview-image'}
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
              />
            </span>
          </button>
          <div className="photo-upload-side-actions">
            {uploading ? (
              <div
                className="photo-upload-remove photo-upload-spinner-shell"
                aria-label="Uploading photo"
                role="status"
              > 
                <span className="photo-upload-spinner" aria-hidden="true" />
              </div>
            ) : (
              <IconButton
                className="photo-upload-remove"
                aria-label={removing ? 'Removing photo' : 'Remove photo'}
                title={removing ? 'Removing photo' : 'Remove photo'}
                onClick={() => setConfirmRemoveOpen(true)}
                disabled={disabled || busy}
              >
                <TrashIcon />
              </IconButton>
            )}
            {statusText ? (
              <span className="photo-upload-side-status">{statusText}</span>
            ) : null}
          </div>
          <FullscreenPhotoViewer
            open={viewerOpen}
            src={imagePreview}
            onClose={() => setViewerOpen(false)}
            viewerLabel={`${label} — preview`}
          />
          <ConfirmDialog
            open={confirmRemoveOpen}
            title="Remove photo?"
            message="Are you sure you want to remove this photo?"
            confirmLabel="Remove"
            onCancel={() => setConfirmRemoveOpen(false)}
            onConfirm={() => {
              setConfirmRemoveOpen(false);
              void handleRemoveImage();
            }}
          />
        </div>
      )}
      {error ? (
        <div className="photo-upload-error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
};

export default PhotoUpload;
